import P from 'parsimmon';
import { z } from 'zod';

import { assertNever } from '../../assertNever';
import { StepDefinitionId } from '../../BrandedIds';
import { Temporal } from '../../Temporal';
import { EvaluatorFunctionContext, FunctionContext, PerCallFunctionContext } from './FunctionContext';
import {
  FunctionDefinition,
  FunctionLibrary,
  getFunctionSignatureWithoutReturnType,
  Parameter,
  SpreadParam,
  spreadType,
} from './FunctionDefinition';
import { BOOLEAN_FUNCTIONS } from './functions/boolean';
import { COMPARISON_FUNCTIONS } from './functions/comparison';
import { DATES_AND_TIMES_FUNCTIONS } from './functions/datesAndTimes';
import { LOAN_FUNCTIONS } from './functions/loan';
import { LOGICAL_FUNCTIONS } from './functions/logical';
import { MATH_FUNCTIONS } from './functions/math';
import { STRING_FUNCTIONS } from './functions/strings';
import { WORKFLOW_FUNCTIONS } from './functions/workflow';
import { ExpressionType, Type, TypeFrom, TypeMap, TYPES } from './Types';

export class ExpressionEvaluator {
  constructor(private readonly ctx: EvaluatorFunctionContext) {}

  public static readonly FUNCTIONS = {
    ...BOOLEAN_FUNCTIONS,
    ...MATH_FUNCTIONS,
    ...DATES_AND_TIMES_FUNCTIONS,
    ...COMPARISON_FUNCTIONS,
    ...STRING_FUNCTIONS,
    ...LOGICAL_FUNCTIONS,
    ...WORKFLOW_FUNCTIONS,
    ...LOAN_FUNCTIONS,
  } satisfies FunctionLibrary;

  private static readonly FUNCTIONS_SANS_RETURN_TYPE = Object.fromEntries(
    Object.values(this.FUNCTIONS).map(
      (
        functionDefinition: FunctionDefinition<
          [...Parameter<Type<unknown>>[]] | SpreadParam<Parameter<Type<unknown>>>,
          Type<unknown>,
          string
        >,
      ): [
        string,
        FunctionDefinition<
          [...Parameter<Type<unknown>>[]] | SpreadParam<Parameter<Type<unknown>>>,
          Type<unknown>,
          string
        >,
      ] => [
        getFunctionSignatureWithoutReturnType({
          name: functionDefinition.name,
          parameterTypes:
            'spread' in functionDefinition.parameters
              ? { spread: functionDefinition.parameters.spread.type }
              : functionDefinition.parameters.map((p) => p.type),
        }),
        functionDefinition,
      ],
    ),
  );

  public evaluate<T extends ExpressionType>(
    expression: Expression<T>,
    ctx: PerCallFunctionContext,
  ): TypeFrom<(typeof TYPES)[T]> {
    const parsedExpression = ExpressionEvaluator.parse(expression);
    return ExpressionEvaluator.evaluateWithContext(parsedExpression, {
      ...this.ctx,
      now: ctx.now,
    }) as TypeFrom<(typeof TYPES)[T]>;
  }

  public static evaluateWithContext(expression: ExpressionNode, ctx: FunctionContext): unknown {
    switch (expression.name) {
      case 'BOOLEAN_LITERAL':
      case 'NUMBER_LITERAL':
      case 'STRING_LITERAL':
        return expression.value;
      case 'FUNCTION_APPLICATION':
        const functionDefinition = ExpressionEvaluator.lookupFunctionDefinition(expression);
        return (functionDefinition.implementation as any)(
          ctx,
          ...expression.value.parameters.map((p, i) => {
            if (!('spread' in functionDefinition.parameters) && functionDefinition.parameters[i].isLazy) {
              return () => ExpressionEvaluator.evaluateWithContext(p, ctx);
            }
            return ExpressionEvaluator.evaluateWithContext(p, ctx);
          }),
        );
      default:
        return assertNever(expression);
    }
  }

  public static parse(expression: string): ExpressionNode {
    const result = ExpressionEvaluator.EXPRESSION_LANGUAGE.expression
      .trim(P.whitespace.many())
      .skip(P.eof)
      .parse(expression);
    if (result.status === false) {
      throw new Error(
        `Failed to parse expression: ${result.expected} at line ${result.index.line} column ${result.index.column}`,
      );
    }
    return result.value;
  }

  private static readonly EXPRESSION_LANGUAGE = P.createLanguage<ExpressionLanguageSpec>({
    booleanLiteral: () =>
      P.regexp(/true|false/)
        .map((n) => n === 'true')
        .desc('BOOLEAN_LITERAL')
        .node('BOOLEAN_LITERAL')
        .map((n) => ({ ...n, type: TYPES.boolean.name })),
    numberLiteral: () =>
      P.regexp(/-?(0|[1-9][0-9]*)([.][0-9]+)?([eE][+-]?[0-9]+)?/)
        .map((n) => +n)
        .desc('NUMBER_LITERAL')
        .node('NUMBER_LITERAL')
        .map((n) => ({ ...n, type: TYPES.number.name })),
    stringLiteral: () =>
      P.regexp(/"((?:\\.|.)*?)"/, 1)
        .map(interpretEscapes)
        .desc('STRING_LITERAL')
        .node('STRING_LITERAL')
        .map((n) => ({ ...n, type: TYPES.string.name })),
    functionApplication: ({ expression }) =>
      P.seq(
        P.regexp(/[a-zA-Z_][a-zA-Z0-9_]*/).desc('IDENTIFIER'),
        P.string('('),
        P.sepBy(expression, ARGUMENT_SEPARATOR).skip(ARGUMENT_SEPARATOR.atMost(1)).trim(P.whitespace.many()),
        P.string(')'),
      )
        .map(([name, , parameters]) => ({ name, parameters }))
        .desc('FUNCTION_APPLICATION')
        .node('FUNCTION_APPLICATION')
        .map((n) => {
          const functionDefinition = ExpressionEvaluator.lookupFunctionDefinition(n);
          return { ...n, type: functionDefinition.returnType.name as ExpressionType };
        }),
    expression: ({ functionApplication, numberLiteral, stringLiteral, booleanLiteral }) =>
      P.alt(numberLiteral, stringLiteral, booleanLiteral, functionApplication),
  });

  private static lookupFunctionDefinition(
    functionApplicationNode: Pick<FunctionApplicationNode, 'value' | 'start'>,
  ): FunctionDefinition<
    [...Parameter<Type<unknown>>[]] | SpreadParam<Parameter<Type<unknown>>>,
    Type<unknown>,
    string
  > {
    const signatures = [...this.getAllPossibleSignaturesWithoutReturnType(functionApplicationNode.value)];
    for (const signature of signatures) {
      const functionDefinition = this.FUNCTIONS_SANS_RETURN_TYPE[signature];
      if (functionDefinition != null) {
        return functionDefinition;
      }
    }

    throw new Error(
      `Function with signature ${signatures[0]} not defined at line ${functionApplicationNode.start.line} column ${functionApplicationNode.start.column}`,
    );
  }

  private static *getAllPossibleSignaturesWithoutReturnType({
    parameters,
    name,
  }: FunctionApplicationNode['value']): Generator<string> {
    // try first to naively look up the function signature using the given parameter types
    yield getFunctionSignatureWithoutReturnType({
      name,
      parameterTypes: parameters.map((p) => TYPES[p.type]),
    });

    // if all parameters of the same type it could match a spread
    const [first, ...rest] = parameters;

    if (parameters.length >= 1 && rest.every((p) => p.type === first.type)) {
      yield getFunctionSignatureWithoutReturnType({
        name,
        parameterTypes: spreadType(TYPES[first.type]),
      });
    }
  }

  public getNextTimeExpressionWillChange(
    expression: string,
    perCallCtx: PerCallFunctionContext,
    options: { changeTo?: boolean } = {},
  ): Temporal.ZonedDateTime | undefined {
    const ctx: FunctionContext = { ...this.ctx, now: perCallCtx.now };
    const parsedExpression = ExpressionEvaluator.parse(expression);

    const currentValue = ExpressionEvaluator.evaluateWithContext(parsedExpression, ctx);

    // If changeTo is specified and the expression already evaluates to that value, return undefined
    if (options.changeTo !== undefined && currentValue === options.changeTo) {
      return undefined;
    }

    // For expressions that don't change with time, return undefined
    if (
      ExpressionEvaluator.expressionIsAlwaysTrue(expression) ||
      ExpressionEvaluator.expressionIsAlwaysFalse(expression)
    ) {
      return undefined;
    }

    // Get all timestamps from the expression and sort them
    const timestamps = this.getTimestampsFromExpression(parsedExpression, ctx)
      .filter((t) => Temporal.ZonedDateTime.compare(ctx.now(), t) <= 0)
      .sort((a, b) => Temporal.ZonedDateTime.compare(a, b));

    // Find the first timestamp where the expression changes value
    for (const timestamp of timestamps) {
      const valueAtTimestamp = ExpressionEvaluator.evaluateWithContext(parsedExpression, {
        ...ctx,
        now: () => timestamp,
      });

      // If changeTo is specified, look for that specific value
      if (options.changeTo !== undefined && valueAtTimestamp === options.changeTo) {
        return timestamp;
      }
      if (valueAtTimestamp !== currentValue) {
        // Otherwise, look for any change from the current value
        return timestamp;
      }
    }

    return undefined;
  }

  /**
   * Get all timestamps from an expression that could cause it to change value.
   */
  private getTimestampsFromExpression(expression: ExpressionNode, ctx: FunctionContext): Temporal.ZonedDateTime[] {
    switch (expression.name) {
      case 'BOOLEAN_LITERAL':
      case 'NUMBER_LITERAL':
      case 'STRING_LITERAL':
        return [];
      case 'FUNCTION_APPLICATION': {
        const timestamps: Temporal.ZonedDateTime[] = [];

        if (expression.type === 'zonedDateTime' || expression.type === 'plainDate') {
          try {
            const result = ExpressionEvaluator.evaluateWithContext(expression, ctx);
            if (result instanceof Temporal.ZonedDateTime) {
              timestamps.push(result);
            }
            if (result instanceof Temporal.PlainDate) {
              timestamps.push(result.toZonedDateTime(ctx.workflowTimeZone));
            }
          } catch (error) {
            // Skip this timestamp if evaluation fails
          }
        }

        // Recursively search all parameters for timestamps
        const parameterTimestamps = expression.value.parameters.flatMap((p) =>
          this.getTimestampsFromExpression(p, ctx),
        );
        timestamps.push(...parameterTimestamps);

        return timestamps;
      }
      default:
        return assertNever(expression);
    }
  }

  public static expressionIsAlwaysTrue(expression: string): boolean {
    const parsedExpression = ExpressionEvaluator.parse(expression);
    return ExpressionEvaluator.expressionNodeIsAlwaysTrue(parsedExpression);
  }

  private static expressionNodeIsAlwaysTrue(expression: ExpressionNode): boolean {
    switch (expression.name) {
      case 'BOOLEAN_LITERAL':
        return expression.value === true;
      case 'NUMBER_LITERAL':
      case 'STRING_LITERAL':
        return false;
      case 'FUNCTION_APPLICATION': {
        const { name, parameters } = expression.value;

        // Handle logical operators with recursive evaluation
        if (name === 'not') {
          return ExpressionEvaluator.expressionNodeIsAlwaysFalse(parameters[0]);
        }

        if (name === 'or') {
          // Recursively check all parameters - if any is always true, the result is always true
          return parameters.some((p) => ExpressionEvaluator.expressionNodeIsAlwaysTrue(p));
        }

        if (name === 'and') {
          // Recursively check all parameters - if all are always true, the result is always true
          return parameters.every((p) => ExpressionEvaluator.expressionNodeIsAlwaysTrue(p));
        }

        return false;
      }
      default:
        return assertNever(expression);
    }
  }

  public static expressionIsAlwaysFalse(expression: string): boolean {
    const parsedExpression = ExpressionEvaluator.parse(expression);
    return ExpressionEvaluator.expressionNodeIsAlwaysFalse(parsedExpression);
  }

  public static findExpressionNodes(
    expression: string,
    predicate: (node: ExpressionNode) => boolean,
  ): ExpressionNode[] {
    const parsedExpression = ExpressionEvaluator.parse(expression);
    return ExpressionEvaluator.findExpressionNodesInTree(parsedExpression, predicate);
  }

  private static findExpressionNodesInTree(
    expression: ExpressionNode,
    predicate: (node: ExpressionNode) => boolean,
  ): ExpressionNode[] {
    const results: ExpressionNode[] = [];
    if (predicate(expression)) {
      results.push(expression);
    }

    switch (expression.name) {
      case 'FUNCTION_APPLICATION': {
        const { parameters } = expression.value;
        for (const parameter of parameters) {
          results.push(...ExpressionEvaluator.findExpressionNodesInTree(parameter, predicate));
        }
        break;
      }
      case 'BOOLEAN_LITERAL':
      case 'NUMBER_LITERAL':
      case 'STRING_LITERAL':
        break;
      default:
        assertNever(expression);
    }

    return results;
  }

  private static expressionNodeIsAlwaysFalse(expression: ExpressionNode): boolean {
    switch (expression.name) {
      case 'BOOLEAN_LITERAL':
        return expression.value === false;
      case 'NUMBER_LITERAL':
      case 'STRING_LITERAL':
        return false;
      case 'FUNCTION_APPLICATION': {
        const { name, parameters } = expression.value;

        // Handle logical operators with recursive evaluation
        if (name === 'not') {
          return ExpressionEvaluator.expressionNodeIsAlwaysTrue(parameters[0]);
        }

        if (name === 'and') {
          // Recursively check all parameters - if any is always false, the result is always false
          return parameters.some((p) => ExpressionEvaluator.expressionNodeIsAlwaysFalse(p));
        }

        if (name === 'or') {
          // Recursively check all parameters - if all are always false, the result is always false
          return parameters.every((p) => ExpressionEvaluator.expressionNodeIsAlwaysFalse(p));
        }

        return false;
      }
      default:
        return assertNever(expression);
    }
  }

  public static assertType<T extends ExpressionType>(expression: string, type: T): Expression<T> {
    const parsed = ExpressionEvaluator.parse(expression);
    if (parsed.type !== type) {
      throw new Error(`Expression is not of type ${type}`);
    }
    return expression as Expression<T>;
  }

  public static assertBoolean = (expression: string): Expression<'boolean'> =>
    ExpressionEvaluator.assertType(expression, 'boolean');

  private static template = (strings: TemplateStringsArray, ...values: InterpolatableValue[]): Expression =>
    strings.reduce((acc, str, i) => {
      const value = values[i];
      if (isNestedInterpolatableValue(value)) {
        return acc + str + value.expression;
      }

      if (value == null) {
        return acc + str;
      }

      if (value instanceof Temporal.ZonedDateTime) {
        return `${acc + str}zonedDateTime(${JSON.stringify(value.toString())})`;
      }

      if (value instanceof Temporal.Duration) {
        return `${acc + str}duration(${JSON.stringify(value.toString())})`;
      }

      if (value instanceof Temporal.PlainDate) {
        return `${acc + str}plainDate(${JSON.stringify(value.toString())})`;
      }

      if (value instanceof Temporal.PlainTime) {
        return `${acc + str}plainTime(${JSON.stringify(value.toString())})`;
      }

      if (value instanceof Date) {
        return `${acc + str}date(${JSON.stringify(value.toISOString())})`;
      }

      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return acc + str + JSON.stringify(value);
      }

      return assertNever(value);
    }, '') as Expression;

  public static nest = (expression: Expression): NestedInterpolatableValue => ({ __type: 'nested', expression });

  public static boolean = (strings: TemplateStringsArray, ...values: InterpolatableValue[]): Expression<'boolean'> =>
    ExpressionEvaluator.assertType(ExpressionEvaluator.template(strings, ...values), 'boolean');

  public static plainDate = (
    strings: TemplateStringsArray,
    ...values: InterpolatableValue[]
  ): Expression<'plainDate'> =>
    ExpressionEvaluator.assertType(ExpressionEvaluator.template(strings, ...values), 'plainDate');

  public static zonedDateTime = (
    strings: TemplateStringsArray,
    ...values: InterpolatableValue[]
  ): Expression<'zonedDateTime'> =>
    ExpressionEvaluator.assertType(ExpressionEvaluator.template(strings, ...values), 'zonedDateTime');

  public static string = (strings: TemplateStringsArray, ...values: InterpolatableValue[]): Expression<'string'> =>
    ExpressionEvaluator.assertType(ExpressionEvaluator.template(strings, ...values), 'string');

  public static number = (strings: TemplateStringsArray, ...values: InterpolatableValue[]): Expression<'number'> =>
    ExpressionEvaluator.assertType(ExpressionEvaluator.template(strings, ...values), 'number');

  public static duration = (strings: TemplateStringsArray, ...values: InterpolatableValue[]): Expression<'duration'> =>
    ExpressionEvaluator.assertType(ExpressionEvaluator.template(strings, ...values), 'duration');

  public static getReferencedStepDefinitionIds(_selfId: StepDefinitionId, _expression: Expression): StepDefinitionId[] {
    // TODO implement
    return [];
  }

  public static expressionCanBeTrueWithoutStepDependency(_expression: Expression): boolean {
    // TODO implement
    return true;
  }
}

type InterpolatableValue = TypeMap[keyof TypeMap] | NestedInterpolatableValue;

type NestedInterpolatableValue = { __type: 'nested'; expression: Expression };

const isNestedInterpolatableValue = (value: unknown): value is NestedInterpolatableValue =>
  typeof value === 'object' && value != null && '__type' in value && value.__type === 'nested';

const ARGUMENT_SEPARATOR = P.string(',').trim(P.whitespace.many());

interface ExpressionLanguageSpec {
  booleanLiteral: BooleanLiteralNode;
  numberLiteral: NumberLiteralNode;
  stringLiteral: StringLiteralNode;
  functionApplication: FunctionApplicationNode;
  expression: ExpressionNode;
}

type BooleanLiteralNode = P.Node<'BOOLEAN_LITERAL', boolean> & { type: (typeof TYPES.boolean)['name'] };
type NumberLiteralNode = P.Node<'NUMBER_LITERAL', number> & { type: (typeof TYPES.number)['name'] };
type StringLiteralNode = P.Node<'STRING_LITERAL', string> & { type: (typeof TYPES.string)['name'] };
type FunctionApplicationNode<T extends ExpressionType = ExpressionType> = P.Node<
  'FUNCTION_APPLICATION',
  { name: string; parameters: ExpressionNode<T>[] }
> & {
  type: T;
};
type ExpressionNode<T extends ExpressionType = ExpressionType> =
  | BooleanLiteralNode
  | NumberLiteralNode
  | StringLiteralNode
  | FunctionApplicationNode<T>;

export type Expression<T extends ExpressionType = ExpressionType> = string & { __brand: 'Expression'; __type: T };
export const Expression = <T extends ExpressionType>(
  type: T,
): z.ZodPipe<z.ZodString, z.ZodTransform<Awaited<Expression<T>>, string>> =>
  z.string().transform((val, ctx): Expression<T> => {
    try {
      const parsed = ExpressionEvaluator.parse(val);
      if (parsed.type !== type) {
        ctx.addIssue({
          code: 'custom',
          message: `Expression is not of type ${type} (got ${parsed.type}): ${val}`,
        });
        return z.NEVER;
      }
      return val as Expression<T>;
    } catch (e) {
      ctx.addIssue({ code: 'custom', message: `Expression is not valid: ${e}` });
      return z.NEVER;
    }
  });

// Turn escaped characters into real ones (e.g. "\\n" becomes "\n").
const ESCAPES = Object.create(null);
ESCAPES.b = '\b';
ESCAPES.f = '\f';
ESCAPES.n = '\n';
ESCAPES.r = '\r';
ESCAPES.t = '\t';

function interpretEscapes(str: string) {
  return str.replace(/\\(u[0-9a-fA-F]{4}|[^u])/, (_, escape) => {
    const type = escape.charAt(0);
    const hex = escape.slice(1);
    if (type === 'u') {
      return String.fromCharCode(parseInt(hex, 16));
    }
    if (type in ESCAPES) {
      return ESCAPES[type as keyof typeof ESCAPES];
    }
    return type;
  });
}
